/*
=============================================================================
*** Custom CRM Models ***

This code was manually created to enhance TypeScript typing and facilitate
development until a code generation tool or library, such as crmtyped, is
implemented to automate model generation.
=============================================================================
*/

import { IFieldVisibility } from "global/Visibility/IFieldVisibility.type";
import {
    account as IXrmTypes_account,
    bcbs_agencydetail as IXrmTypes_bcbs_agencydetail,
    bcbs_carrier as IXrmTypes_bcbs_carrier,
    bcbs_config_attribute as IXrmTypes_bcbs_config_attribute,
    bcbs_config_category as IXrmTypes_bcbs_config_category,
    bcbs_config_optionsetitem as IXrmTypes_bcbs_config_optionsetitem,
    bcbs_config_section as IXrmTypes_bcbs_config_section,
    bcbs_groupconfigurationsnapshot as IXrmTypes_bcbs_groupconfigurationsnapshot,
    bcbs_groupconfigurationsnapshotevent as IXrmTypes_bcbs_groupconfigurationsnapshotevent,
    bcbs_grouplibraryprocess as IXrmTypes_bcbs_grouplibraryprocess,
    bcbs_sbc as IXrmTypes_bcbs_sbc,
    contact as IXrmTypes_contact,
    systemuser as IXrmTypes_systemuser
} from "./CRM";
import { IPickItem } from "./Extension";
import {
    IBenefit as FAM_IBenefit,
    IFeesAndPremiums as FAM_IFeesAndPremiums,
    TGuid as FAM_TGuid
} from "./FlexibleAttributeModel";
import { ERoles } from "./Role.type";
import { ETeams } from "./Team.type";

export interface ILabelDefinition {
    Label: string;
    LanguageCode?: number;
    IsManaged?: boolean;
    MetadataId?: string;
    HasChanged?: null;
}

export interface ILabel {
    LocalizedLabels: ILabelDefinition[]
    UserLocalizedLabel: ILabelDefinition[] | ILabelDefinition | null
}

export interface IOptionOSD {
    Value: number;
    Label: ILabel;
    Color: string | null;
    IsManaged: boolean,
    ExternalValue?: string | null,
    ParentValues: any[],
    Tag: any,
    IsHidden: boolean,
    MetadataId?: string | null,
    HasChanged?: string | null,
    Description: ILabel;

}

export interface IOptionSetDefinition {
    Name?: string;
    Options: IOptionOSD[]
}

export interface ISecurityRole {
    roleid: FAM_TGuid;
    name: ERoles;
}
export interface ITeam {
    teamid: FAM_TGuid;
    ownerid: FAM_TGuid;
    name: ETeams;
    businessUnitId: string;
}

export interface ITeamQuery extends ITeam {
    teamroles_association: ISecurityRole[];
}

export interface ISystemUserQuery extends IXrmTypes_systemuser {
    systemuserroles_association: ISecurityRole[];
    teammembership_association: ITeam[]
}

export interface IAccountExtended extends IXrmTypes_account {
    '_bcbs_accountexecutiveid_value@OData.Community.Display.V1.FormattedValue': string;
    '_bcbs_agentcode@OData.Community.Display.V1.FormattedValue': string;
    '_bcbs_clientrelationshipmanagerid_value@OData.Community.Display.V1.FormattedValue': string;
    '_bcbs_groupleaderid_value@OData.Community.Display.V1.FormattedValue': string;
    '_bcbs_partnerplanid_value@OData.Community.Display.V1.FormattedValue': string;
    '_bcbs_renewalrepid_value@OData.Community.Display.V1.FormattedValue': string;
    '_bcbs_salesrepid_value@OData.Community.Display.V1.FormattedValue': string;
    '_bcbs_servicerepid_value@OData.Community.Display.V1.FormattedValue': string;
    '_bcbs_siccodeid_value@OData.Community.Display.V1.FormattedValue': string;
    '_bcbs_strategicaccountexecutiveid_value@OData.Community.Display.V1.FormattedValue': string;
    '_parentaccountid_value@OData.Community.Display.V1.FormattedValue': string;
    'bcbs_anniversarydate@OData.Community.Display.V1.FormattedValue': string;
    'bcbs_lineofbusiness@OData.Community.Display.V1.FormattedValue': string;
    'bcbs_renewaldate@OData.Community.Display.V1.FormattedValue': string;
    'customertypecode@OData.Community.Display.V1.FormattedValue': string;
    '_bcbs_clientimplementationmanagerid_value@OData.Community.Display.V1.FormattedValue': string;

}

export interface Ibcbs_carrierExtended extends IXrmTypes_bcbs_carrier {
    '_bcbs_parentid_value@OData.Community.Display.V1.FormattedValue': string
}


export interface Ibcbs_enrollmentdetailExtended extends IXrmTypes_bcbs_carrier {
    'ownerid@odata.bind'?: string
}

export type IBenefitList = IPickItem<FAM_IBenefit>[];
export type IFeesAndPremiumList = IPickItem<FAM_IFeesAndPremiums>[];


export interface bcbs_config_categoryNavCopay {
    bcbs_name: string;
    bcbs_config_categoryid: FAM_TGuid;
    bcbs_CopayCoinOption_Config_Category: ICopayCoin[]
}
export interface ICopayCoin {
    bcbs_name: string;
    bcbs_copaycoinoptionid: FAM_TGuid;
    bcbs_copaycoinsurancetypecode: number;
    bcbs_iscurrencyonly: boolean | null;
    bcbs_ispercentageonly: boolean | null;
    statuscode: number;
}


export type TCategoryName =
    'Group Configuration' |
    'Plan' |
    'Ancillary' |
    'COBRA' |
    'FSA' |
    'HRA' |
    'HSA' |
    'LSA' |
    'Medical' |
    'Pharmacy' |
    'Vision' |
    'Dental' |
    'Fees and Premiums' |
    'Stop Loss' |
    'Accounts Receivable' |
    'Group Number' |
    'Non-Standard' |
    'Non-Standard Template' |
    'Telehealth' |
    'Florida Prime' |
    'Capital Prime' |
    'Quote Request'


export interface Ibcbs_groupconfigurationsnapshoteventUpdate extends Partial<IXrmTypes_bcbs_groupconfigurationsnapshotevent> {
    [key: string]: any;
}

export interface IUpdatable_bcbs_groupconfigurationsnapshot extends Partial<IXrmTypes_bcbs_groupconfigurationsnapshot> {
    'bcbs_EnrollmentDetailsid@odata.bind'?: string | null;
    'bcbs_Releaseid@odata.bind'?: string | null;
    'bcbs_UWConfirmedParentid@odata.bind'?: string | null;
    'bcbs_Opportunityid@odata.bind'?: string | null;
    'bcbs_groupid@odata.bind'?: string | null;
    'bcbs_OriginatingSnapshotid@odata.bind'?: string | null;
    'bcbs_OriginatingSnapshot2id@odata.bind'?: string | null;
    'bcbs_GroupCollectFinancialConfigurationid@odata.bind'?: string | null;
    'ownerid@odata.bind'?: string | null;
    'bcbs_ReleaseUpgradedFromid@odata.bind'?: string | null;
}

export interface Ibcbs_config_attribute_extended extends IXrmTypes_bcbs_config_attribute {
    /**
     * Indicates if the attribute is related to cancellation, cascaded from tab.
     */
    bcbs_iscancellation?: boolean;

    visibility?: IFieldVisibility;
    // Lookup: GUID -> Category
    NonStandardCustomCategories?: Record<string, IXrmTypes_bcbs_config_category>;
    NonStandardCustomSections?: Record<string, IXrmTypes_bcbs_config_section>;
}

export interface Ibcbs_config_optionsetitem_extended extends IXrmTypes_bcbs_config_optionsetitem {
    visibility?: IFieldVisibility;
}


export interface Ibcbs_grouplibraryprocess_extended extends IXrmTypes_bcbs_grouplibraryprocess {
    ['bcbs_processstate@OData.Community.Display.V1.FormattedValue']?: string;
}


export interface Ibcbs_groupconfigurationsnapshot_extended extends IXrmTypes_bcbs_groupconfigurationsnapshot {
    ['_bcbs_originatingsnapshotid_value@OData.Community.Display.V1.FormattedValue']: string;
    ['_bcbs_originatingsnapshot2id_value@OData.Community.Display.V1.FormattedValue']: string;
    ['bcbs_fundingtypecode@OData.Community.Display.V1.FormattedValue']: string;
    ['bcbs_changetypecode@OData.Community.Display.V1.FormattedValue']: string;
    ['_createdby_value@OData.Community.Display.V1.FormattedValue']: string;
    ['_modifiedby_value@OData.Community.Display.V1.FormattedValue']: string;
    ['_bcbs_releaseid_value@OData.Community.Display.V1.FormattedValue']: string;
}

export interface Ibcbs_agencydetail_extended extends IXrmTypes_bcbs_agencydetail {
    agentAccount?: IXrmTypes_account;
    agentContact?: IXrmTypes_contact;
}

export interface ISystemUserExtended extends IXrmTypes_systemuser {
    'bcbs_repcode@OData.Community.Display.V1.FormattedValue': string;
}

export interface IPicked_bcbs_sbc extends Pick<IXrmTypes_bcbs_sbc, 'bcbs_name' | 'bcbs_planid' | 'bcbs_sbcid' | 'bcbs_sbctype' | 'statuscode'> {

}

export interface IQuery_bcbs_sbc {
    "@odata.etag": string;
    bcbs_name: string,
    bcbs_sbcid: string,
    modifiedon: string,

    // id of group configuration
    _bcbs_group_name_value?: string,
    bcbs_group_name: {
        bcbs_effectivedate: string,
        bcbs_groupconfigurationsnapshotid: string
    }
}

// Configuration Relationship picklist enums
// Derived from the bcbs_ConfigurationRelationship entity definition in Dynamics.
// The interface itself is auto-generated in CRM.ts — these enums are kept here
// because the model builder does not generate TypeScript enums for option sets.

/**
 * bcbs_origintypecode picklist values
 * bcbs_configurationrelationship_bcbs_origintypecode
 */
export enum EOriginTypeCode {
    Benefit = 808900000,
}

/**
 * bcbs_relationshiptypecode picklist values
 * bcbs_configurationrelationship_bcbs_relationshiptypecode
 */
export enum ERelationshipTypeCode {
    AssociationTemplate = 808900000,
}

/**
 * bcbs_targetactioncode picklist values
 * bcbs_configurationrelationship_bcbs_targetactioncode (Required)
 */
export enum ETargetActionCode {
    Import = 808900000,
    Sync = 808900001,
    Clone = 808900002,
}

// OData bind helpers not generated by the model builder — extend the auto-generated interface.
export interface Ibcbs_configurationrelationship_extended {
    'bcbs_OriginSnapshotId@odata.bind'?: string;
    'bcbs_TargetSnapshotId@odata.bind'?: string;
}